import { MenuOutlined } from '@ant-design/icons';
import { Avatar, Badge, Button, Dropdown, Layout, Menu } from 'antd';
import defaultShopLogoUrl from 'assets/logos/pitstop-logo.png';
import { chatUtils } from 'helpers';
import _ from 'lodash';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Link, withRouter } from 'react-router-dom';
import { AppStore, CurrentUserStore, ShopStore } from 'stores';
import styled from 'styled-components';

const { Header: AntdHeader } = Layout;

const StyledAntDHeader = styled(AntdHeader)`
  &&& {
    padding: 0px 20px;
    position: fixed;
    top: 0;
    z-index: 200;
    width: 100%;
    height: fit-content;
    display: flex;
    justify-content: space-between;

    @media (max-width: 576px) {
      padding: ${(props) =>
        props.isexcludedpathname === 'false' ? '0px' : '0px 20px'};
      position: ${(props) =>
        props.isexcludedpathname === 'false' && 'relative'};
      flex-direction: ${(props) =>
        props.isexcludedpathname === 'false' && 'column'};
    }
  }
`;

const StyledAvatar = styled(Avatar)`
  &&& {
    width: 120px;
    height: 45px;
    margin: auto;

    span {
      width: 100%;
      height: 100%;
    }

    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: scale-down;
    }
  }
`;

const MenuContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 0px 10px;

  #menu {
    width: 100%;
  }
`;

const MenuStyled = styled(Menu)`
  line-height: 64px;
  float: right;
  .ant-menu-item {
    color: hsla(0, 0%, 100%, 0.65) !important;
    &::after {
      border: 0px !important;
    }
  }
  .ant-menu-item-selected {
    color: #fff !important;
    background-color: #123fbf !important;
  }
  .ant-menu-item:hover {
    color: #fff !important;
  }
  @media (max-width: 576px) {
    .ant-menu-item {
      padding: 0px 10px;
    }
  }

  .ant-menu-overflow-item {
    .ant-menu-submenu-title {
      color: white !important;
    }
  }
`;

const StyledBadge = styled(Badge)`
  && {
    border-radius: 2px;
    display: inline-block;
    vertical-align: middle;
    margin-left: 5px;

    p {
      font-size: 12px;
    }
  }
`;

const Header = ({ location, history, toggleSideBar }) => {
  const toggleSidebar = async () => {
    toggleSideBar();
  };

  const getAppUnreadMessagesCounts = () => {
    let unreadMessages = [];
    let messagesKeys = Array.from(CurrentUserStore.notifications.keys());

    _.forEach(messagesKeys, (key) => {
      let unreadMessagesCount = chatUtils.getAppUnreadMessagesCounts(
        CurrentUserStore.user.name,
        CurrentUserStore.notifications.get(key).data
      );

      let user =
        CurrentUserStore.user.role === 'dealership'
          ? chatUtils.getDriverInfo(
              toJS(CurrentUserStore.fleetManagersDrivers).data,
              key
            )
          : chatUtils.getFleetManagerInfo(
              toJS(CurrentUserStore.fleetManagersDrivers).data
            );

      if (unreadMessagesCount > 0) {
        unreadMessages.push({
          userId: key,
          name: `${user.first_name} ${user.last_name}`,
          data: CurrentUserStore.notifications.get(key).data,
        });
      }
    });

    return unreadMessages;
  };

  const getMenu = () => {
    let unreadMessages = getAppUnreadMessagesCounts();

    if (_.isEmpty(unreadMessages)) {
      return [{ key: 'no-messages', label: 'You have no messages' }];
    }

    return unreadMessages
      .map((message) => {
        let unreadMessagesCount = chatUtils.getAppUnreadMessagesCounts(
          CurrentUserStore.user.name,
          message.data
        );

        if (unreadMessagesCount === 0) return null;

        return {
          key: message.userId,
          label: (
            <Link to="/pages/chat">
              <b>{`${message.name.capitalizeFirstLetter()}`}</b> sent{' '}
              <b>{`${unreadMessagesCount}`}</b> messages
            </Link>
          ),
        };
      })
      .filter(Boolean);
  };

  const getAdminHeader = () => {
    let headers = [
      {
        key: '/FAQ',
        label: 'Learning Center',
        onClick: () => history.push('/FAQ'),
      },
      {
        key: '/profile',
        label: 'Profile',
        onClick: () => history.push('/profile'),
      },
    ];

    if (!AppStore.isOnGeotabDashboard()) {
      headers.push({
        key: 'logout',
        label: AppStore.loggedIn.get() ? 'Logout' : 'Login',
        onClick: () => {
          AppStore.showLogoutModal.set(true);
        },
      });
    }

    return headers;
  };

  const getUserHeader = () => {
    let unreadMessages = getAppUnreadMessagesCounts();

    let headers = [
      {
        key: '/notifications',
        label: (
          <Dropdown menu={{ items: getMenu() }} placement="bottomCenter" arrow>
            <span>
              Notifications
              <StyledBadge count={unreadMessages.length} />
            </span>
          </Dropdown>
        ),
      },
      {
        key: '/profile',
        label:
          CurrentUserStore.user.role === 'technician' ? 'Settings' : 'Profile',
        onClick: () => history.push('/profile'),
      },
    ];

    if (!AppStore.isOnGeotabDashboard()) {
      headers.push({
        key: 'logout',
        label: AppStore.loggedIn.get() ? 'Logout' : 'Login',
        onClick: () => {
          AppStore.showLogoutModal.set(true);
        },
      });
    }

    return headers;
  };

  const isExcludedPathname = () => {
    let { pathname } = history.location;
    return ['/login', '/register'].includes(pathname);
  };

  let sections = getAdminHeader();
  // if (CurrentUserStore.loaded) {
  //   if (!CurrentUserStore.user) {
  //     sections = [];
  //   } else {
  //     switch (CurrentUserStore.user.role) {
  //       case 'admin':
  //       case 'dealership':
  //         sections = getAdminHeader();
  //         break;
  //       case 'user':
  //       default:
  //         sections = getUserHeader();
  //     }
  //   }
  // }

  let shopLogo = ShopStore.currentShop.logoUrl || defaultShopLogoUrl;

  return (
    <StyledAntDHeader
      isexcludedpathname={isExcludedPathname().toString()}
      className="header"
    >
      <StyledAvatar
        shape="square"
        alt="logo"
        src={shopLogo}
        onClick={() => history.push(AppStore.loggedIn.get() ? '/' : '/login')}
      />

      <MenuContainer>
        <Button
          ghost
          size="large"
          onClick={() => toggleSidebar()}
          style={{
            color: 'white',
            borderWidth: '0px',
            margin: 'auto 0px',
          }}
        >
          <MenuOutlined style={{ fontSize: 24, color: 'white' }} />
        </Button>

        <div id="menu">
          <ul
            style={{
              background: 'transparent',
              borderBottom: 'none',
              lineHeight: '64px',
              float: 'right',
              display: 'flex',
              listStyle: 'none',
              margin: 0,
              padding: 0,
            }}
          >
            {sections.map((section) => (
              <li
                key={section.key}
                style={{
                  display: 'inline-block',
                  padding: '0 20px',
                  color:
                    location.pathname === section.key
                      ? '#fff'
                      : 'hsla(0,0%,100%,.65)',
                  backgroundColor:
                    location.pathname === section.key
                      ? '#123fbf'
                      : 'transparent',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  // if Logout, show the logout modal
                  if (section.key === 'logout') {
                    AppStore.showLogoutModal.set(true);
                  } else {
                    history.push(section.key);
                  }
                }}
              >
                {section.icon && (
                  <span style={{ marginRight: '8px' }}>{section.icon}</span>
                )}
                {section.label}
                {section.badge && (
                  <StyledBadge>
                    <p>{section.badge}</p>
                  </StyledBadge>
                )}
              </li>
            ))}
            {sections.length > 5 && (
              <li
                style={{
                  display: 'inline-block',
                  padding: '0 20px',
                  color: 'white',
                }}
              >
                <MenuOutlined style={{ fontSize: 24, color: 'white' }} />
              </li>
            )}
          </ul>
        </div>
      </MenuContainer>
    </StyledAntDHeader>
  );
};

Header.propTypes = {
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  toggleSideBar: PropTypes.func,
};

export default withRouter(observer(Header));
